import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { AsyncButton } from '@/components/common/AsyncButton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { FileUploadDialog } from '@/components/dialogs/FileUploadDialog';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import { ApiError } from '@/api/client/abpError';
import { usePermission } from '@/permissions/usePermission';
import { symbolsApi } from '@/api/services/onboarding';
import type {
  CreateSymbolDto,
  CreateSymbolsDatatableDto,
  MissingSymbolDto,
  SymbolDto,
  UpdateSymbolDto,
} from '@/api/types/onboarding';
import { renderNormalNumber } from '../helpers';
import { MissingSymbolsModal } from '../MissingSymbolsModal';

const P = 'User.Onboarding.Symbols';
const SPREAD_BET = 'Spread Bet';

interface Draft {
  name: string;
  symbolType: string;
  sbUnderlyingCurrency: string;
  assetClass: string;
  base: string;
  quote: string;
  quoteAssetClass: string;
  country: string;
  contractSize: string;
  dataSource: string;
  digits: string;
}

const emptyDraft = (): Draft => ({
  name: '', symbolType: '', sbUnderlyingCurrency: '', assetClass: '', base: '', quote: '',
  quoteAssetClass: '', country: '0', contractSize: '', dataSource: '', digits: '',
});

export function SymbolsTab() {
  const toast = useToast();
  const can = usePermission();
  const canCreate = can(`${P}.Create`);
  const canUpdate = can(`${P}.Update`);
  const canDelete = can(`${P}.Delete`);
  const canDownloadExcel = can(`${P}.DownloadExcel`);
  const canUpload = can(`${P}.UploadExcel`);

  const dropdowns = useQuery({ queryKey: ['onboarding', 'symbols', 'dropdown'], queryFn: symbolsApi.getDropdownData });
  const opts = dropdowns.data;

  const [newDraft, setNewDraft] = useState<Draft | null>(null);
  const [editId, setEditId] = useState<string | null>(null); // symbolId
  const [editDraft, setEditDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SymbolDto | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [missing, setMissing] = useState<MissingSymbolDto[] | null>(null);
  // Container (outside the table scroll) that the inline-edit select2 dropdowns render
  // into, so they aren't clipped by the table's overflow.
  const [ddParent, setDdParent] = useState<HTMLElement | null>(null);

  const table = useServerDataTable<SymbolDto, Partial<CreateSymbolsDatatableDto>>({
    queryKey: ['onboarding', 'symbols'],
    fetcher: (req) => symbolsApi.getListForDataTable(req as CreateSymbolsDatatableDto),
    pageSize: 5,
  });

  // On first load, after dropdowns ready, check for missing symbols.
  const missingQuery = useQuery({
    queryKey: ['onboarding', 'symbols', 'missing'],
    queryFn: symbolsApi.getMissingSymbols,
    enabled: dropdowns.isSuccess,
  });
  useEffect(() => {
    if (missingQuery.data && missingQuery.data.length > 0 && missing === null) {
      setMissing(missingQuery.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingQuery.data]);

  const checkMissingAfter = async () => {
    try {
      const m = await symbolsApi.getMissingSymbols();
      if (m.length > 0) setMissing(m);
    } catch {
      /* ignore */
    }
  };

  const symbolTypeOpts = (opts?.symbolTypes ?? []).map((s) => ({ value: s.id, label: s.name }));
  // Asset classes for create exclude Debt (handled server-side list); AS-IS uses the full dropdown set.
  const assetClassOpts = (opts?.assetClasses ?? []).map((s) => ({ value: s.id, label: s.name }));
  const quoteAssetClassOpts = (opts?.quoteAssetClasses ?? []).map((s) => ({ value: s.id, label: s.name }));
  const countryOpts = useMemo(() => [{ value: 0, label: 'N/A' }, ...(opts?.countries ?? []).map((c) => ({ value: c.id, label: c.name }))], [opts]);
  const dsOpts = (opts?.dataSources ?? []).map((s) => ({ value: s.id, label: s.name }));

  const isSpreadBet = (symbolTypeId: string) => symbolTypeOpts.find((s) => s.value === Number(symbolTypeId))?.label === SPREAD_BET;

  const buildCreate = (d: Draft): CreateSymbolDto => {
    const country = Number.parseInt(d.country, 10);
    const spreadBet = isSpreadBet(d.symbolType);
    return {
      name: d.name,
      symbolType: Number.parseInt(d.symbolType, 10),
      sbUnderlyingCurrency: spreadBet ? d.sbUnderlyingCurrency : null,
      assetClass: Number.parseInt(d.assetClass, 10),
      base: d.base,
      quote: d.quote,
      quoteAssetClass: Number.parseInt(d.quoteAssetClass, 10),
      country: country === 0 || Number.isNaN(country) ? null : country,
      contractSize: Number.parseInt(d.contractSize.replace(/,/g, ''), 10) || 0,
      digits: Number.parseInt(d.digits, 10) || 0,
      dataSource: Number(d.dataSource),
    };
  };

  const saveNew = async () => {
    if (!newDraft) return;
    setSaving(true);
    try {
      await symbolsApi.create(buildCreate(newDraft));
      toast.success('New Symbol Added Successfully');
      setNewDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to add symbol.');
    } finally {
      setSaving(false);
    }
  };

  const beginEdit = (row: SymbolDto) => {
    setEditId(row.symbolId);
    setEditDraft({
      name: row.name,
      symbolType: String(symbolTypeOpts.find((o) => o.label === row.symbolType)?.value ?? ''),
      sbUnderlyingCurrency: row.sbUnderlyingCurrency ?? '',
      assetClass: String(assetClassOpts.find((o) => o.label === row.assetClass)?.value ?? ''),
      base: row.base,
      quote: row.quote,
      quoteAssetClass: String(quoteAssetClassOpts.find((o) => o.label === row.quoteAssetClass)?.value ?? ''),
      country: String(countryOpts.find((o) => o.label === row.country)?.value ?? '0'),
      contractSize: String(row.contractSize),
      dataSource: String(dsOpts.find((o) => o.label === row.dataSource)?.value ?? ''),
      digits: String(row.digits),
    });
  };

  const saveEdit = async () => {
    if (!editId || !editDraft) return;
    setSaving(true);
    try {
      const c = buildCreate(editDraft);
      const dto: UpdateSymbolDto = {
        name: c.name,
        symbolType: c.symbolType,
        sbUnderlyingCurrency: c.sbUnderlyingCurrency,
        assetClass: c.assetClass,
        base: c.base,
        quote: c.quote,
        quoteAssetClass: c.quoteAssetClass,
        country: c.country,
        digits: c.digits,
        dataSource: c.dataSource,
      };
      await symbolsApi.update(editId, dto);
      toast.success('Saved successfully.');
      setEditId(null);
      setEditDraft(null);
      table.refetch();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update symbol.');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await symbolsApi.delete(deleteTarget.symbolId);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete.');
    } finally {
      setDeleteTarget(null);
      table.refetch();
    }
  };

  const doUpload = async (file: File) => {
    try {
      const result = await symbolsApi.uploadSymbols(file);
      table.refetch();
      if (result.success === false) {
        toast.error(result.message || 'Upload failed.');
        return;
      }
      const created = result.data.created;
      const errors = result.data.errors;
      if (created === 0 && errors.length === 0) {
        toast.info('No symbols to process.');
      } else {
        const msg =
          `${created} symbol${created !== 1 ? 's' : ''} added successfully.` +
          (errors.length > 0 ? ` — ${errors.length} symbol${errors.length !== 1 ? 's' : ''} to be defined.` : '');
        if (created > 0) toast.success(msg);
        else toast.warning(msg);
      }
      setUploadOpen(false);
      await checkMissingAfter();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to upload file.');
    }
  };

  const sel = (d: Draft | null, k: keyof Draft, set: (d: Draft) => void, options: { value: string | number; label: string }[]) =>
    d && (
      <Select2
        value={d[k]}
        options={options}
        placeholder="N/A"
        allowClear={false}
        searchable={false}
        disabled={saving}
        dropdownParent={ddParent}
        onChange={(v) => set({ ...d, [k]: v ?? '' })}
      />
    );
  const txt = (d: Draft | null, k: keyof Draft, set: (d: Draft) => void, extra?: { disabled?: boolean }) =>
    d && <input className="form-control form-control-sm" value={d[k]} disabled={saving || extra?.disabled} onChange={(e) => set({ ...d, [k]: e.target.value })} />;

  const sbCell = (d: Draft | null, set: (d: Draft) => void) =>
    d && <input className="form-control form-control-sm" value={isSpreadBet(d.symbolType) ? d.sbUnderlyingCurrency : ''} disabled={saving || !isSpreadBet(d.symbolType)} onChange={(e) => set({ ...d, sbUnderlyingCurrency: e.target.value })} />;

  const columns = useMemo<DataGridColumn<SymbolDto>[]>(
    () => [
      {
        key: 'name', title: 'Symbol', className: 'text-start', value: (r) => r.name,
        cell: (r) => (editId === r.symbolId ? txt(editDraft, 'name', setEditDraft) : r.name),
      },
      {
        key: 'symbolType', title: 'Symbol Type', className: 'text-start', value: (r) => r.symbolType,
        cell: (r) => (editId === r.symbolId ? sel(editDraft, 'symbolType', setEditDraft, symbolTypeOpts) : r.symbolType),
      },
      {
        key: 'sbUnderlyingCurrency', title: 'SB Underlying Currency', className: 'text-start', filterable: false, value: (r) => r.sbUnderlyingCurrency ?? '',
        cell: (r) => (editId === r.symbolId ? sbCell(editDraft, setEditDraft) : (r.sbUnderlyingCurrency ?? '')),
      },
      {
        key: 'assetClass', title: 'Asset Class', className: 'text-start', value: (r) => r.assetClass,
        cell: (r) => (editId === r.symbolId ? sel(editDraft, 'assetClass', setEditDraft, assetClassOpts) : r.assetClass),
      },
      {
        key: 'base', title: 'Base', className: 'text-start', value: (r) => r.base,
        cell: (r) => (editId === r.symbolId ? txt(editDraft, 'base', setEditDraft) : r.base),
      },
      {
        key: 'quote', title: 'Quote', className: 'text-start', value: (r) => r.quote,
        cell: (r) => (editId === r.symbolId ? txt(editDraft, 'quote', setEditDraft) : r.quote),
      },
      {
        key: 'quoteAssetClass', title: 'Quote Asset Class', className: 'text-start', value: (r) => r.quoteAssetClass,
        cell: (r) => (editId === r.symbolId ? sel(editDraft, 'quoteAssetClass', setEditDraft, quoteAssetClassOpts) : r.quoteAssetClass),
      },
      {
        key: 'country', title: 'Country', className: 'text-start', value: (r) => r.country ?? '',
        cell: (r) => (editId === r.symbolId ? sel(editDraft, 'country', setEditDraft, countryOpts) : (r.country ?? '')),
      },
      {
        key: 'contractSize', title: 'Contract Size', className: 'text-end', value: (r) => r.contractSize,
        cell: (r) => (editId === r.symbolId ? renderNormalNumber(r.contractSize) /* read-only on edit */ : renderNormalNumber(r.contractSize)),
      },
      {
        key: 'dataSource', title: 'Connector', className: 'text-start', filterable: false, value: (r) => r.dataSource,
        cell: (r) => (editId === r.symbolId ? sel(editDraft, 'dataSource', setEditDraft, dsOpts) : r.dataSource),
      },
      {
        key: 'digits', title: 'Digits', className: 'text-end', filterable: false, value: (r) => r.digits,
        cell: (r) => (editId === r.symbolId ? r.digits /* read-only on edit */ : r.digits),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editId, editDraft, saving, opts],
  );

  const toolbar = (
    <div className="d-flex gap-2">
      {canDownloadExcel && (
        <AsyncButton className="btn btn-success btn-sm" icon="fa fa-download" title="Download all instruments as Excel"
          onClick={() => symbolsApi.downloadInstruments().catch((e) => toast.error(e instanceof ApiError ? e.message : 'Download failed.'))}>
          Download Instruments
        </AsyncButton>
      )}
      {canCreate && (
        <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-plus" title="Create new record" onClick={() => { if (!newDraft) setNewDraft(emptyDraft()); }}>
          Create New
        </AsyncButton>
      )}
      {canUpload && (
        <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-upload" title="Upload instruments from Excel (will override existing)" onClick={() => setUploadOpen(true)}>
          Upload
        </AsyncButton>
      )}
    </div>
  );

  const footerTop = newDraft ? (
    <tr className="onb-new-row">
      <td>{txt(newDraft, 'name', setNewDraft)}</td>
      <td>{sel(newDraft, 'symbolType', setNewDraft, symbolTypeOpts)}</td>
      <td>{sbCell(newDraft, setNewDraft)}</td>
      <td>{sel(newDraft, 'assetClass', setNewDraft, assetClassOpts)}</td>
      <td>{txt(newDraft, 'base', setNewDraft)}</td>
      <td>{txt(newDraft, 'quote', setNewDraft)}</td>
      <td>{sel(newDraft, 'quoteAssetClass', setNewDraft, quoteAssetClassOpts)}</td>
      <td>{sel(newDraft, 'country', setNewDraft, countryOpts)}</td>
      <td>{txt(newDraft, 'contractSize', setNewDraft)}</td>
      <td>{sel(newDraft, 'dataSource', setNewDraft, dsOpts)}</td>
      <td>{txt(newDraft, 'digits', setNewDraft)}</td>
      <td>
        <div className="d-flex gap-1">
          <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveNew} disabled={saving} />
          <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => setNewDraft(null)}><i className="fa fa-times" /></button>
        </div>
      </td>
    </tr>
  ) : undefined;

  const rowActions = (canUpdate || canDelete)
    ? (row: SymbolDto) => (
        <div className="d-flex gap-1">
          {canUpdate && editId !== row.symbolId && (
            <button type="button" className="btn btn-sm btn-outline-primary" title="Edit" onClick={() => beginEdit(row)}><i className="fa fa-pencil" /></button>
          )}
          {editId === row.symbolId && (
            <>
              <AsyncButton className="btn btn-sm btn-outline-primary" icon="fa fa-check" title="Save" onClick={saveEdit} disabled={saving} />
              <button type="button" className="btn btn-sm btn-outline-danger" title="Cancel" disabled={saving} onClick={() => { setEditId(null); setEditDraft(null); }}><i className="fa fa-times" /></button>
            </>
          )}
          {canDelete && editId !== row.symbolId && (
            <button type="button" className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => setDeleteTarget(row)}><i className="fa fa-trash" /></button>
          )}
        </div>
      )
    : undefined;

  return (
    <div ref={setDdParent}>
      <DataGrid
        columns={columns}
        data={table.rows}
        rowKey={(r) => r.id}
        card
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={table.setColumnFilter}
        toolbarStart={toolbar}
        includeExcel={canDownloadExcel}
        excelFileName="Symbols"
        footerTop={footerTop}
        rowActions={rowActions}
        rowClassName={(r) => (editId === r.symbolId ? 'sfc-detail-editing' : undefined)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data available'}
        server={{
          pageIndex: table.pageIndex,
          pageSize: table.pageSize,
          pageCount: table.pageCount,
          totalCount: table.totalCount,
          onPageChange: table.setPageIndex,
          onPageSizeChange: table.setPageSize,
        }}
      />

      <FileUploadDialog
        show={uploadOpen}
        title="Upload Symbols"
        confirmLabel="Upload"
        noFileMessage="Please select a file"
        onClose={() => setUploadOpen(false)}
        onUpload={doUpload}
      />

      {missing && opts && (
        <MissingSymbolsModal
          rows={missing}
          dropdowns={opts}
          onClose={() => setMissing(null)}
          onAdded={() => table.refetch()}
        />
      )}

      <ConfirmDialog
        show={deleteTarget != null}
        message={deleteTarget ? `Delete symbol '${deleteTarget.name}'?` : ''}
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
